<template>
  <div class="campaign-preview-wrapper" v-show="!isLoading">
    <campaign
      :campaignData="campaignData"
      :staticData="staticData"
      :campaignImages="campaignImages"
    />
    <form @submit.prevent="onCaptchaExecute" action="#" class="form" ref="form">
      <div class="row">
        <div class="col-sm-12">
          <div class="text-left text-danger" v-if="errors.has('internalError')">
            There was an error processing your personal information. Please try
            again later.
          </div>
        </div>
      </div>
      <div class="margin-bottom-20 margin-top-20">
        <div class="row recaptcha-row">
          <div class="recaptcha-wrapper">
            <vue-recaptcha
              ref="recaptcha"
              @verify="onCaptchaVerified"
              @expired="onCaptchaExpired"
              size="invisible"
              badge="bottomleft"
              :sitekey="recaptchaSiteKey"
            >
            </vue-recaptcha>
            <div
              class="invalid-feedback-red"
              v-if="errors.has('recaptcha_token')"
            >
              {{ errors.get("recaptcha_token") }}
            </div>
          </div>
        </div>
        <div class="row buttons-wrapper" v-if="!isLoading">
          <div class="row margin-top-50 buttons-row">
            <div class="form-group col-xs-12 col-md-3 col-lg-3">
              <button name="Save as draft"
                type="button"
                @click="saveAsDraftAction"
                class="btn hh-btn back-btn btn-responsive"
              >
                Save as Draft
              </button>
            </div>
            <div class="form-group col-xs-12 col-md-3 col-lg-3">
              <button name="Continue to bank info"
                type="submit"
                :disabled="!isFormReady"
                class="btn save-btn hh-btn-green btn-responsive"
              >
                Continue to Bank Info
              </button>
            </div>
          </div>
          <div class="row back-row" @click="backAction">
            <div class="col-md-12 text-center margin-bottom-20">
            	<img src="/dist/site/static/icons/go_back.svg" style="transform: rotate(180deg);" alt="go back">		
              <span class="hh-back-btn">Go Back</span>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import InputLabel from "../form/elements/InputLabel";
import Campaign from "./Campaign";
import VueRecaptcha from "vue-recaptcha";
import constants from "../../config";
import { scrollTo } from "../../utils/componentHelper";

export default {
  name: "CampaignPreview",

  components: {
    InputLabel,
    Campaign,
    VueRecaptcha,
  },

  props: {
    campaignData: Object,
    campaignImages: Object,
    staticData: {
      states: Object,
      defaultLogoPath: String,
      defaultLogoName: String,
    },
  },

  data: () => {
    return {
      loading: true,
      dataAnalysed: false,
      forceRecompute: 0,
      campaignSlug: null,
      componentsLoadingStatus: {},
      recaptchaSiteKey: null,
      formData: {
        informationType: "campaign-preview",
        method: "put",
        withFiles: false,
        step: 4,

        recaptcha_token: null,
      },
    };
  },

  beforeMount() {
    this.campaignSlug = this.campaignData.slug;
    this.recaptchaSiteKey = constants.RECAPTCHA_INVISIBLE_SITE_KEY;

    this.loading = false;
  },

  mounted() {
    var el = document.querySelector("body");
	  el.children.item("app").classList.add('margin-bottom-0');

    var child = el.children.item("row margin-bottom-50 margin-top-50 buttons-wrapper").children[1];
    child.classList.add('bg-preview');
  },

  destroyed() {
	  var el = document.querySelector("body");
	  el.children.item("app").classList.remove('margin-bottom-0');

    var child = el.children.item("row margin-bottom-50 margin-top-50 buttons-wrapper").children[1];
    child.classList.remove('bg-preview');
  },

  methods: {
    ...mapActions("campaign", ["storeInformation", "saveAsDraft"]),

    cancelAction() {
      window.location = "/dashboard";
    },

    backAction() {
      this.$emit("navigateBack");
    },

    skipAction() {
      this.$emit("navigateNext");
    },

    onCaptchaVerified: async function (recaptchaToken) {
      this.formData.recaptcha_token = recaptchaToken;
      await this.submitForm();
    },
    onCaptchaExpired: function () {
      this.$refs.recaptcha.reset();
    },
    onCaptchaExecute() {
      this.$refs.recaptcha.execute();
    },

    submitForm() {
      this.loading = true;
      this.formData.campaignSlug = this.campaignSlug;

      this.storeInformation(this.formData).then(() => {
        if (this.status === "success") {
          this.$emit("formSubmitted");
        } else {
          this.onCaptchaExpired();
          scrollTo(this.$refs["form"]);
        }
        this.forceRecompute++;
        this.loading = false;
      });
    },

    saveAsDraftAction() {
      this.loading = true;

      this.saveAsDraft(this.campaignSlug).then(() => {
        this.forceRecompute++;
        if (this.saveAsDraftData.status === "success") {
          this.cancelAction();
        }
        this.loading = false;
      });
    },
  },

  computed: {
    isDataFetched() {
      return !_.isEmpty(this.campaignData);
    },

    storeInformationData: {
      get() {
        return this.$store.getters["campaign/storeInformationData"](
          this.campaignSlug,
          this.formData.informationType,
          this.forceRecompute
        );
      },
    },

    status: {
      get() {
        return this.$store.getters["campaign/storeInformationStatus"](
          this.campaignSlug,
          this.formData.informationType,
          this.forceRecompute
        );
      },
    },

    errors: {
      get() {
        return this.$store.getters["campaign/storeInformationErrors"](
          this.campaignSlug,
          this.formData.informationType,
          this.forceRecompute
        );
      },
    },

    saveAsDraftData: {
      get() {
        return this.$store.getters["campaign/saveAsDraftData"](
          this.campaignSlug,
          this.forceRecompute
        );
      },
    },

    isLoading() {
      for (let component in this.componentsLoadingStatus) {
        if (this.componentsLoadingStatus[component]) {
          return true;
        }
      }

      return this.loading;
    },

    isFormReady() {
      return !this.loading;
    },
  },

  watch: {
    isLoading(to, from) {
      this.$emit("componentLoading", "campaignComponent", to);
    },
  },
};
</script>

<style lang="scss" scoped>
.g-recaptcha::v-deep {
  margin-right: -65px;
}

.recaptcha-wrapper {
  float: right;
  width: 55%;
  margin-top: 20px;
}

.recaptcha-row {
  position: relative;
}

.buttons-wrapper {
  position: absolute;
  left: 10px;
  right: 10px;
  padding: 20px;
  background:#F5F7FA;
}


.campaign-preview-wrapper {
	margin-bottom: 211px;
}

.buttons-row {
	display: flex;
	justify-content: center;
}

 .bg-preview {
	background-color: #8A8A8A !important
 }

@media screen and (max-width: 991px) {
  .recaptcha-wrapper {
    width: 80% !important;
  }

  .hh-back-btn {
    font-size: 16px;
    line-height: 16px;
    text-align: center;
    color: #acacac;
  }

  .buttons-row {
	flex-direction: column;
 }

 .buttons-wrapper {
	border-bottom: 1px solid #D0D0D0;
	left: 0;
	right: 0;
 }

 .campaign-preview-wrapper {
 	margin-bottom: 287px;
 }
}
</style>
